import { Controller } from "@hotwired/stimulus"

// Fill-time searchable dropdown for a fabric_reference question (BPO, Vendor, …).
//
// The hidden input IS the form field's answer (what gets stored + flows into ERP
// mappings); the visible search box is UI only and shows the selected label. The
// server (fabric_options endpoint) does the searching + tenant scoping — we never
// hold the dataset client-side.
//
// Cascade: when this question is filtered by another (e.g. BPO filtered by
// Vendor), the supplying controller dispatches a "fabric-reference:change" event
// on document with { fieldId, value }; this controller listens and, if that
// fieldId matches its filteredByFieldId, re-scopes its search and drops any stale
// selection. Each controller knows its own submission field id (fieldId).
export default class extends Controller {
  static targets = ["input", "hidden", "dropdown", "clear"]
  static values = {
    url: String,
    fieldId: String,
    filteredByFieldId: String,
    editable: Boolean
  }

  connect() {
    this.filterValue = ""
    this.debounceTimer = null

    this.boundOutside = this.handleOutsideClick.bind(this)
    document.addEventListener("click", this.boundOutside)
    this.boundCascade = this.handleCascade.bind(this)
    document.addEventListener("fabric-reference:change", this.boundCascade)

    // Seed the filter value from the supplying question's current selection so a
    // reloaded page already scopes correctly.
    if (this.filteredByFieldIdValue) {
      const supplier = document.querySelector(
        `[data-fabric-reference-field-id-value="${this.filteredByFieldIdValue}"] [data-fabric-reference-target="hidden"]`
      )
      if (supplier && supplier.value) this.filterValue = supplier.value
    }

    // Re-display a stored answer with its label (resolve mode) instead of the raw id.
    if (this.hiddenTarget.value) this.resolveLabel(this.hiddenTarget.value)
  }

  disconnect() {
    document.removeEventListener("click", this.boundOutside)
    document.removeEventListener("fabric-reference:change", this.boundCascade)
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
  }

  search() {
    if (!this.editableValue) return

    // Typing invalidates any prior selection until a new option is picked.
    this.hiddenTarget.value = ""
    this.clearTarget.hidden = true

    const query = this.inputTarget.value.trim()
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.fetchOptions(query), 250)
  }

  async fetchOptions(query) {
    const data = await this.request({ q: query })
    if (data) this.renderDropdown(data.options || [])
  }

  async resolveLabel(value) {
    const data = await this.request({ value })
    const option = (data && data.options && data.options[0]) || null
    if (option) {
      this.inputTarget.value = option.label
      if (this.editableValue) this.clearTarget.hidden = false
    } else {
      // Couldn't resolve (out of scope / removed) — show the raw stored id.
      this.inputTarget.value = value
    }
  }

  async request(params) {
    const url = new URL(this.urlValue, window.location.origin)
    Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v))
    if (this.filterValue) url.searchParams.set("filter_value", this.filterValue)

    try {
      const res = await fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      if (!res.ok) return null
      return await res.json()
    } catch (_e) {
      return null
    }
  }

  select(event) {
    const value = event.currentTarget.dataset.value
    const label = event.currentTarget.dataset.label
    this.hiddenTarget.value = value
    this.inputTarget.value = label
    this.clearTarget.hidden = false
    this.closeDropdown()
    this.commit(value)
  }

  clear() {
    this.hiddenTarget.value = ""
    this.inputTarget.value = ""
    this.clearTarget.hidden = true
    this.closeDropdown()
    this.commit("")
    if (this.editableValue) this.inputTarget.focus()
  }

  // Notify autosave (bubbling change on the hidden answer input) AND any
  // dependent fabric-reference question that this question's value changed.
  commit(value) {
    this.hiddenTarget.dispatchEvent(new Event("change", { bubbles: true }))
    document.dispatchEvent(
      new CustomEvent("fabric-reference:change", { detail: { fieldId: this.fieldIdValue, value } })
    )
  }

  handleCascade(event) {
    if (!this.filteredByFieldIdValue) return
    if (String(event.detail.fieldId) !== String(this.filteredByFieldIdValue)) return

    // The supplying question changed → re-scope and drop our now-stale selection.
    this.filterValue = event.detail.value || ""
    if (this.hiddenTarget.value || this.inputTarget.value) {
      this.hiddenTarget.value = ""
      this.inputTarget.value = ""
      this.clearTarget.hidden = true
      this.commit("")
    }
    this.closeDropdown()
  }

  renderDropdown(options) {
    this.dropdownTarget.innerHTML = ""

    if (options.length === 0) {
      this.dropdownTarget.innerHTML = '<div class="vendor-search-empty">No matches</div>'
      this.dropdownTarget.hidden = false
      return
    }

    options.forEach((opt) => {
      const item = document.createElement("button")
      item.type = "button"
      item.className = "vendor-search-item"
      item.dataset.value = opt.value
      item.dataset.label = opt.label
      item.dataset.action = "click->fabric-reference#select"
      item.textContent = opt.label
      this.dropdownTarget.appendChild(item)
    })
    this.dropdownTarget.hidden = false
  }

  closeDropdown() {
    this.dropdownTarget.hidden = true
    this.dropdownTarget.innerHTML = ""
  }

  handleOutsideClick(event) {
    if (!this.element.contains(event.target)) this.closeDropdown()
  }
}
